/**
 * Hierarchical back — named parent paths (not browser history).
 * Golden rule: one predictable parent per non-root surface.
 * Absolute platform `/orgs` (Organizace) returns null (shell shows disabled Zpět — no parent)
 * unless the browse visit stack has a prior step (see browseVisitStack.ts).
 * Platform `/` home redirects to Nakupovat/Obchody — treat like those leaves.
 * Nakupovat = `/browse` (browse root; no hierarchical parent).
 * Oblíbené = `/favorites` (guest and signed-in hub) — parents to Nakupovat `/browse`
 * when browse on. Legacy index `/account/shop` redirects to `/favorites` (SPA never
 * paints chrome there); hierarchical parent for that path is `/favorites`. Nested
 * `/account/shop/...` → `/favorites`.
 * Platform `/sign-in` parents to `/shops` (never org picker); prior browse still
 * wins via browseVisitStack when applicable.
 * Terminal success screens parent to shop (exit via ←; POP still → shop).
 * All other retail surfaces — including former “tab roots” — expose Zpět
 * so the fixed shell header stays universal with bottom nav.
 */

import { tenantRelativePath } from '../app/bottomNavActiveTab.js';
import { isPlatformChromeTenant } from '../app/platformChromeTenant.js';
import {
  resolveCustomerSignInPath,
  resolveCustomerSignUpPath,
} from '../features/auth/customerAuthPaths.js';
import { PLATFORM_ORGS_PATH, resolvePlatformFavoritesParentPath } from '../features/shop/resolveShopHref.js';
import {
  resolveAllowedBackNavigation,
  resolvePostAuthFallbackPath,
  type JourneyId,
} from './navigationEngine.js';
import type { MasterScreenId } from '../registry/masterScreenRegistry.types.js';

export interface HierarchicalBackTarget {
  /** Absolute app path (pathname + optional hash). */
  readonly parentPath: string;
}

export type SystemBackDecision =
  | { readonly action: 'allow' }
  | { readonly action: 'redirect'; readonly to: string };

export interface PathJourneyGate {
  readonly journeyId: JourneyId;
  readonly masterScreenId: MasterScreenId;
}

/**
 * Options for named hierarchical ← chrome (scanner catalog search, etc.).
 */
export interface HierarchicalBackOptions {
  /** Location search (`?…` or raw) — used by scanner → catalog parent. */
  readonly search?: string;
}

/**
 * Options for OS / browser POP.
 * In-flow SoT is HierarchicalBackFlow: `consumeStepBack` (same-URL) + `stepBackTo`
 * (cross-route). Dead options `flowStep` / `paymentRailUiStep` / `paymentActive`
 * were removed — they never refined the redirect gate.
 */
export interface SystemBackDecisionOptions {
  readonly journeyId?: JourneyId | null;
  readonly masterScreenId?: MasterScreenId | null;
  /**
   * In-flow step parent path for POP. When set, preferred over hierarchical
   * for redirect (after terminal overrides). Avoids useless same-URL redirects.
   */
  readonly stepBackTo?: string | null;
  /** Forwarded to hierarchical resolver (scanner catalog parent). */
  readonly search?: string;
}

function tenantBase(tenantCode: string): string {
  return `/${encodeURIComponent(tenantCode)}`;
}

/**
 * Org slug for Wave C auth parent `?tenant=` — real tenantCode, else `?tenant=` from search.
 */
function resolveAuthParentTenantCode(
  tenantCode: string,
  search?: string,
): string {
  if (!isPlatformChromeTenant(tenantCode) && tenantCode.trim().length > 0) {
    return tenantCode;
  }
  if (search === undefined || search.length === 0) {
    return tenantCode;
  }
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const fromQuery = new URLSearchParams(raw).get('tenant')?.trim() ?? '';
  if (fromQuery.length > 0 && !isPlatformChromeTenant(fromQuery)) {
    return fromQuery;
  }
  return tenantCode;
}

function matchTenantRel(
  rel: string,
  pattern: RegExp,
): RegExpMatchArray | null {
  return rel.match(pattern);
}

function resolveTenantRelative(
  pathname: string,
  tenantCode: string,
): string {
  const trimmedPath = pathname.split('?')[0] ?? pathname;
  if (isPlatformChromeTenant(tenantCode) || tenantCode.trim().length === 0) {
    return trimmedPath;
  }
  return tenantRelativePath(trimmedPath, tenantCode);
}

function resolveShopFallbackPath(tenantCode: string): string {
  if (isPlatformChromeTenant(tenantCode) || tenantCode.trim().length === 0) {
    return '/';
  }
  return resolvePostAuthFallbackPath(tenantCode);
}

/** Ensure leading `?` when search is non-empty after trim; else empty string. */
function normalizeLocationSearch(search?: string): string {
  if (search === undefined) {
    return '';
  }
  const trimmed = search.trim();
  if (trimmed.length === 0) {
    return '';
  }
  return trimmed.startsWith('?') ? trimmed : `?${trimmed}`;
}

/**
 * Wave D — named parents for platform `/account/**`, `/more`, `/orders/find`, `/help`.
 */
function resolvePlatformAccountHierarchicalParent(
  pathname: string,
): HierarchicalBackTarget | null {
  const rel =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  if (rel === '/more' || rel.startsWith('/more/')) {
    return { parentPath: '/shops' };
  }
  if (rel === '/orders/find' || rel.startsWith('/orders/find/')) {
    return { parentPath: '/more' };
  }
  if (rel === '/help' || rel.startsWith('/help/')) {
    return { parentPath: '/more' };
  }
  if (rel === '/account' || rel === '/account/home') {
    return { parentPath: '/more' };
  }

  const orderDetail = matchTenantRel(rel, /^\/account\/orders\/([^/]+)\/?$/);
  if (orderDetail !== null) {
    return { parentPath: '/account/orders' };
  }
  if (rel === '/account/orders' || rel.startsWith('/account/orders/')) {
    return { parentPath: '/more' };
  }

  if (
    rel === '/account/payments/claim' ||
    rel.startsWith('/account/payments/claim/')
  ) {
    return { parentPath: '/account/orders' };
  }
  const paymentQr = matchTenantRel(
    rel,
    /^\/account\/payments\/([^/]+)\/qr\/?$/,
  );
  if (paymentQr !== null) {
    return { parentPath: '/account/orders' };
  }
  if (rel === '/account/payments' || rel.startsWith('/account/payments/')) {
    return { parentPath: '/account/orders' };
  }

  if (
    rel === '/account/account-and-privacy' ||
    rel.startsWith('/account/account-and-privacy/') ||
    rel.startsWith('/account/privacy') ||
    rel.startsWith('/account/settings')
  ) {
    return { parentPath: '/shops' };
  }

  if (
    rel === '/account/donations' ||
    rel.startsWith('/account/donations/') ||
    rel === '/account/recurring-donations' ||
    rel.startsWith('/account/recurring-donations/') ||
    rel === '/account/donate' ||
    rel.startsWith('/account/donate/')
  ) {
    return { parentPath: '/more' };
  }

  if (rel === '/account/shop' || rel === '/account/shop/') {
    return { parentPath: '/favorites' };
  }
  if (rel.startsWith('/account/shop/')) {
    return { parentPath: '/favorites' };
  }

  return { parentPath: '/more' };
}

/**
 * Scanner ← / POP parent: live catalog with preserved query when present
 * (matches ScannerPage `buildShopPath` spirit). Never bare `/{tenant}/shop`.
 */
function resolveScannerCatalogParent(
  tenantCode: string,
  search?: string,
): string {
  const normalized = normalizeLocationSearch(search);
  if (normalized.length > 0) {
    return `${tenantBase(tenantCode)}/shop${normalized}`;
  }
  return resolvePostAuthFallbackPath(tenantCode);
}

/**
 * Infer journey + master-screen gate from the path the user is leaving.
 * Used when JourneyContext is unset / outside JourneyProvider.
 * Master-screen ids are exact registry entries (not prefix families).
 */
export function inferJourneyGateFromPath(
  pathname: string,
  tenantCode: string,
): PathJourneyGate | null {
  const rel = resolveTenantRelative(pathname, tenantCode);

  if (rel.startsWith('/checkout/complete')) {
    return { journeyId: 'J-PHONE-FIRST-PRODUCT', masterScreenId: 'MS-COM-032' };
  }
  if (rel === '/checkout/handoff' || rel.startsWith('/checkout/handoff/')) {
    return { journeyId: 'J-PHONE-FIRST-PRODUCT', masterScreenId: 'MS-COM-030' };
  }
  if (rel.startsWith('/checkout')) {
    return { journeyId: 'J-PHONE-FIRST-PRODUCT', masterScreenId: 'MS-COM-031' };
  }
  if (rel === '/post-kiosk-failure' || rel.startsWith('/post-kiosk-failure/')) {
    return { journeyId: 'J-POST-KIOSK-PRODUCT', masterScreenId: 'MS-COM-034' };
  }
  if (rel === '/post-kiosk' || rel.startsWith('/post-kiosk/')) {
    return { journeyId: 'J-POST-KIOSK-PRODUCT', masterScreenId: 'MS-COM-033' };
  }
  if (rel === '/unavailable' || rel.startsWith('/unavailable/')) {
    return { journeyId: 'J-SHARED', masterScreenId: 'MS-ENTRY-002' };
  }
  if (rel.startsWith('/donate') || rel.startsWith('/account/donate')) {
    return { journeyId: 'J-PHONE-FIRST-DONATION', masterScreenId: 'MS-COM-035' };
  }
  if (rel.startsWith('/card/link')) {
    return { journeyId: 'J-CARD-LINK', masterScreenId: 'MS-AUTH-017' };
  }
  if (
    rel.startsWith('/sign-in') ||
    rel.startsWith('/sign-up') ||
    rel.startsWith('/confirm-email') ||
    rel.startsWith('/forgot-password') ||
    rel.startsWith('/reset-password') ||
    rel.startsWith('/onboarding/')
  ) {
    return { journeyId: 'J-AUTH', masterScreenId: 'MS-AUTH-010' };
  }
  if (rel === '/shop/scanner' || rel.startsWith('/shop/scanner/')) {
    return { journeyId: 'J-SHOP-PURCHASE', masterScreenId: 'MS-HUB-021' };
  }
  if (rel.startsWith('/pickup/verify')) {
    return { journeyId: 'J-PICKUP-VERIFY', masterScreenId: 'MS-PICK-040' };
  }
  return null;
}

/**
 * Resolve the logical parent for in-app ← chrome.
 * Returns `null` only on absolute platform `/orgs` (shell renders disabled Zpět).
 */
export function resolveHierarchicalBackTarget(
  pathname: string,
  tenantCode: string,
  options?: HierarchicalBackOptions,
): HierarchicalBackTarget | null {
  const trimmedPath = pathname.split('?')[0] ?? pathname;

  // Platform Nakupovat (Stage 3 browse root) — no hierarchical parent; visit stack only.
  if (trimmedPath === '/browse' || trimmedPath.startsWith('/browse/')) {
    return null;
  }
  // Platform Obchody — ← back to Organizace (universal chrome).
  if (trimmedPath === '/shops' || trimmedPath.startsWith('/shops/')) {
    return { parentPath: PLATFORM_ORGS_PATH };
  }
  // Platform Oblíbené — ← → Nakupovat when browse on; `/scan` when browse off (no self-loop).
  if (trimmedPath === '/favorites' || trimmedPath.startsWith('/favorites/')) {
    return { parentPath: resolvePlatformFavoritesParentPath() };
  }
  // Platform sign-in — ← → Obchody (shop home), never org picker. Prior browse
  // still wins via browseVisitStack when the user arrived from a stackable path.
  if (trimmedPath === '/sign-in' || trimmedPath.startsWith('/sign-in/')) {
    return { parentPath: '/shops' };
  }
  // Platform auth cluster — Wave C parents emit `?tenant=` when org is known.
  {
    const authTenant = resolveAuthParentTenantCode(tenantCode, options?.search);
    if (trimmedPath === '/confirm-email' || trimmedPath.startsWith('/confirm-email/')) {
      return { parentPath: resolveCustomerSignInPath(authTenant) };
    }
    if (
      trimmedPath.startsWith('/sign-up/pending') ||
      (trimmedPath.startsWith('/sign-up/') && trimmedPath.includes('confirm-email'))
    ) {
      return { parentPath: resolveCustomerSignUpPath(authTenant) };
    }
    if (trimmedPath === '/sign-up' || trimmedPath.startsWith('/sign-up/')) {
      return { parentPath: resolveCustomerSignInPath(authTenant) };
    }
    if (
      trimmedPath.startsWith('/forgot-password') ||
      trimmedPath.startsWith('/reset-password')
    ) {
      return { parentPath: resolveCustomerSignInPath(authTenant) };
    }
    if (trimmedPath.startsWith('/onboarding/set-credentials')) {
      return { parentPath: '/account/account-and-privacy#credentials' };
    }
  }

  // Wave D — platform `/account/**` + `/more` hierarchical parents (chrome SSOT).
  if (
    trimmedPath === '/account' ||
    trimmedPath.startsWith('/account/') ||
    trimmedPath === '/more' ||
    trimmedPath.startsWith('/more/') ||
    trimmedPath === '/orders/find' ||
    trimmedPath.startsWith('/orders/find/') ||
    trimmedPath === '/help' ||
    trimmedPath.startsWith('/help/')
  ) {
    const platformAccountParent = resolvePlatformAccountHierarchicalParent(
      trimmedPath,
    );
    if (platformAccountParent !== null) {
      return platformAccountParent;
    }
  }

  // Organizace root — no parent (home `/` redirects away; not a browse root).
  if (
    trimmedPath === PLATFORM_ORGS_PATH ||
    trimmedPath.startsWith(`${PLATFORM_ORGS_PATH}/`) ||
    trimmedPath === '/' ||
    tenantCode.trim().length === 0
  ) {
    return null;
  }

  if (isPlatformChromeTenant(tenantCode)) {
    return null;
  }

  const base = tenantBase(tenantCode);
  const rel = tenantRelativePath(trimmedPath, tenantCode);
  const shopFallback = resolvePostAuthFallbackPath(tenantCode);

  // Tenant SP picker from Organizace browse — ← → platform orgs.
  if (rel === '/shops' || rel.startsWith('/shops/')) {
    return { parentPath: PLATFORM_ORGS_PATH };
  }

  // Auth cluster — dismiss toward shop (or named auth parent).
  // G3 — login parents use path-tenant `/{tenant}/sign-in` (never `/sign-in?tenant=`).
  if (rel === '/sign-in' || rel.startsWith('/sign-in/')) {
    return { parentPath: shopFallback };
  }
  if (rel === '/confirm-email' || rel.startsWith('/confirm-email/')) {
    return { parentPath: resolveCustomerSignInPath(tenantCode) };
  }
  if (rel === '/sign-up' || rel.startsWith('/sign-up/')) {
    if (rel.startsWith('/sign-up/pending') || rel.includes('confirm-email')) {
      return { parentPath: resolveCustomerSignUpPath(tenantCode) };
    }
    return { parentPath: resolveCustomerSignInPath(tenantCode) };
  }
  if (rel.startsWith('/forgot-password') || rel.startsWith('/reset-password')) {
    return { parentPath: resolveCustomerSignInPath(tenantCode) };
  }
  if (rel.startsWith('/onboarding/set-credentials')) {
    // Wave D — onboarding parent is bare `/account/**` (never `/{tenant}/account`).
    return { parentPath: '/account/account-and-privacy#credentials' };
  }
  if (rel.startsWith('/card/link')) {
    return { parentPath: shopFallback };
  }

  // Post-kiosk — failure + success both expose ← → shop (exit; POP → shop).
  // Non-success invalid/loading/expired may also publish stepBackTo / JourneyShell
  // backTo={shop}; path SoT alone cannot distinguish outcome.
  if (rel === '/post-kiosk-failure' || rel.startsWith('/post-kiosk-failure/')) {
    return { parentPath: shopFallback };
  }
  if (rel === '/post-kiosk' || rel.startsWith('/post-kiosk/')) {
    return { parentPath: shopFallback };
  }

  // Surface unavailable — recover toward shop.
  if (rel === '/unavailable' || rel.startsWith('/unavailable/')) {
    return { parentPath: shopFallback };
  }

  // Recovery surfaces — escape toward shop (same pattern as unavailable).
  if (rel === '/recovery' || rel.startsWith('/recovery/')) {
    return { parentPath: shopFallback };
  }

  // Checkout complete — TERMINAL exit ← → shop (not re-enter payment via /checkout*).
  if (rel.startsWith('/checkout/complete')) {
    return { parentPath: shopFallback };
  }

  // Checkout — exit to shop (includes /checkout/handoff token resolve;
  // step-internal back lives in JourneyShell onBack / stepBackTo).
  if (rel.startsWith('/checkout')) {
    return { parentPath: shopFallback };
  }

  // Scanner depth under shop — preserve ?kioskId= / search; else post-auth fallback.
  if (rel === '/shop/scanner' || rel.startsWith('/shop/scanner/')) {
    return {
      parentPath: resolveScannerCatalogParent(tenantCode, options?.search),
    };
  }
  if (rel === '/scan' || rel.startsWith('/scan/')) {
    return { parentPath: shopFallback };
  }

  // Guest donate — amount root ← → shop (auto-bar matches POP); depth → named parents.
  if (rel === '/donate' || rel === '/donate/') {
    return { parentPath: shopFallback };
  }
  if (rel === '/donate/projects' || rel.startsWith('/donate/projects/')) {
    return { parentPath: `${base}/donate` };
  }
  if (matchTenantRel(rel, /^\/donate\/([^/]+)\/?$/) !== null) {
    return { parentPath: `${base}/donate/projects` };
  }

  // More overflow utilities — Wave D parents are platform `/more`.
  if (rel === '/orders/find' || rel.startsWith('/orders/find/')) {
    return { parentPath: '/more' };
  }
  if (rel === '/help' || rel.startsWith('/help/')) {
    return { parentPath: '/more' };
  }
  if (rel.startsWith('/pickup/verify')) {
    return { parentPath: '/more' };
  }

  // Account — Wave D parents are bare `/account/**` + `/more`.
  if (rel === '/account' || rel === '/account/') {
    return { parentPath: '/more' };
  }

  const orderDetail = matchTenantRel(rel, /^\/account\/orders\/([^/]+)\/?$/);
  if (orderDetail !== null) {
    return { parentPath: '/account/orders' };
  }
  if (rel === '/account/orders' || rel.startsWith('/account/orders/')) {
    return { parentPath: '/more' };
  }

  if (
    rel === '/account/payments/claim' ||
    rel.startsWith('/account/payments/claim/')
  ) {
    return { parentPath: '/account/orders' };
  }
  const paymentQr = matchTenantRel(
    rel,
    /^\/account\/payments\/([^/]+)\/qr\/?$/,
  );
  if (paymentQr !== null) {
    return { parentPath: '/account/orders' };
  }
  // Legacy payments list redirects to orders; treat as orders child.
  if (rel === '/account/payments' || rel.startsWith('/account/payments/')) {
    return { parentPath: '/account/orders' };
  }

  if (
    rel === '/account/account-and-privacy' ||
    rel.startsWith('/account/account-and-privacy/') ||
    rel.startsWith('/account/privacy') ||
    rel.startsWith('/account/settings')
  ) {
    return { parentPath: shopFallback };
  }

  // Donation history lists — More children.
  if (
    rel === '/account/donations' ||
    rel.startsWith('/account/donations/') ||
    rel === '/account/recurring-donations' ||
    rel.startsWith('/account/recurring-donations/')
  ) {
    return { parentPath: '/more' };
  }

  if (
    rel === '/account/shop/sales-points' ||
    rel.startsWith('/account/shop/sales-points/') ||
    rel === '/account/shop/pm' ||
    rel.startsWith('/account/shop/')
  ) {
    return { parentPath: '/favorites' };
  }

  // Former tab roots — universal Zpět toward Obchody / shop fallback / More.
  if (rel === '/more' || rel.startsWith('/more/')) {
    return { parentPath: shopFallback };
  }
  if (rel === '/account/shop' || rel === '/account/shop/') {
    return { parentPath: '/favorites' };
  }
  if (rel === '/account/donate' || rel.startsWith('/account/donate/')) {
    return { parentPath: '/more' };
  }
  if (rel === '/shop' || rel.startsWith('/shop/') || rel === '/home' || rel.startsWith('/home/')) {
    return { parentPath: '/shops' };
  }

  // G13 — product detail `/{store}/{productSlug}` → store catalog `/{tenant}/{store}`.
  // Runs after reserved two-segment surfaces (shop/scanner, donate/*, account/*, …).
  const productDetailMatch = matchTenantRel(rel, /^\/([^/]+)\/([^/]+)\/?$/);
  if (productDetailMatch !== null) {
    const storeSeg = productDetailMatch[1] ?? '';
    if (storeSeg.length > 0) {
      return {
        parentPath: `${base}/${encodeURIComponent(storeSeg)}`,
      };
    }
  }

  return { parentPath: shopFallback };
}

/**
 * OS / browser back (POP) decision for the path the user is leaving.
 * Prefer named parents over raw history — especially pay + terminal success.
 * Journey gates come from live context and/or path inference.
 */
export function resolveSystemBackDecision(
  leftPathname: string,
  tenantCode: string,
  options?: SystemBackDecisionOptions,
): SystemBackDecision {
  const rel = resolveTenantRelative(leftPathname, tenantCode);
  const shopFallback = resolveShopFallbackPath(tenantCode);

  // Terminal — never re-enter payment / post-kiosk resolver via history.
  if (
    rel === '/post-kiosk' ||
    rel.startsWith('/post-kiosk/') ||
    rel.startsWith('/checkout/complete')
  ) {
    return { action: 'redirect', to: shopFallback };
  }

  // In-flow step back: prefer stepBackTo over hierarchical for POP.
  // Same-URL pops are handled by consumeStepBack before this resolver runs.
  const stepBackTo = options?.stepBackTo;
  if (stepBackTo !== null && stepBackTo !== undefined && stepBackTo.length > 0) {
    return { action: 'redirect', to: stepBackTo };
  }

  // Guest /donate amount-at-root: hierarchical parent is shop; POP also exits to shop.
  if (rel === '/donate' || rel === '/donate/') {
    return { action: 'redirect', to: shopFallback };
  }

  const hierarchical = resolveHierarchicalBackTarget(leftPathname, tenantCode, {
    search: options?.search,
  });
  const inferred = inferJourneyGateFromPath(leftPathname, tenantCode);
  const journey = options?.journeyId ?? inferred?.journeyId ?? null;
  const msId = options?.masterScreenId ?? inferred?.masterScreenId ?? null;

  if (journey !== null && msId !== null) {
    const gate = resolveAllowedBackNavigation(journey, msId);
    if (gate === 'blocked' || gate === 'exit-journey') {
      if (hierarchical !== null) {
        return { action: 'redirect', to: hierarchical.parentPath };
      }
      return { action: 'redirect', to: shopFallback };
    }
  }

  if (hierarchical === null) {
    return { action: 'allow' };
  }

  return { action: 'redirect', to: hierarchical.parentPath };
}
